import React, { useEffect, useMemo, useState } from "react";
import {
  BenefitDetailWrapper,
  BenefitFormWrapper,
  BenefitSaveBtn,
  ChooseTypeWrapper,
} from "./styles";
import { BaseDropdown, LargeButton, SuccessModal } from "components/core";
import BenefitSetupDetailSection from "./BenefitSetupDetailSection";
import DependentsCard from "pages/GradeSetup/DependentsCard";
import { Field, Form, Formik } from "formik";
import useFetchWellness from "./useFetchWellness";
import BenefitSetupDetailGrade from "./BenefitSetupDetailGrade";
import { useLocation, useNavigate } from "react-router";
import http from "services/httpService";
import { Ptag } from "components/core/fonts";
import { Box, CircularProgress } from "@mui/material";
import * as Yup from "yup";

const CHOICE_TYPE = [
  { display_name: "Same for All Employees", value: "SAME" },
  { display_name: "Graded as per GMC", value: "GRADED" },
];

const INIT_FORMIK_VALUES = {
  choiceType: "SAME",
  entity_id: "",
  benefit_details: [""],
  vendor_id: "",
  benefit_heading: "",
  benefit_description: "",
  redeem_option_data: "",
  dependents_covered: [],
  grades: [],
};

// Validation schema for required fields
const validationSchema = Yup.object().shape({
  vendor_id: Yup.string().required("*Required"),
  entity_id: Yup.string().when('choiceType', {
    is: 'SAME',
    then: Yup.string().required("*Required"),
    otherwise: Yup.string().nullable()
  }),
  benefit_heading: Yup.string().when('choiceType', {
    is: 'SAME',
    then: Yup.string().required("*Required"),
    otherwise: Yup.string().nullable()
  }),
  benefit_description: Yup.string().when('choiceType', {
    is: 'SAME',
    then: Yup.string().required("*Required"),
    otherwise: Yup.string().nullable()
  }),
  dependents_covered: Yup.array().when('choiceType', {
    is: 'SAME',
    then: Yup.array().min(1, "*Required"),
    otherwise: Yup.array().nullable()
  }),
  grades: Yup.array().when('choiceType', {
    is: 'GRADED',
    then: Yup.array().min(1, "*Required").of(
      Yup.object().shape({
        entity_id: Yup.string().required("*Required"),
        benefit_heading: Yup.string().required("*Required"),
        benefit_description: Yup.string().required("*Required"),
        dependents_covered: Yup.array().min(1, "*Required"),
      })
    ),
    otherwise: Yup.array().nullable()
  })
});

// Function to check if form has valid data
const isFormValid = (values) => {
  if (!values) return false;
  
  // Check if vendor is selected
  if (!values.vendor_id) return false;
  
  // For SAME mode, check main form fields
  if (values.choiceType === 'SAME') {
    return values.entity_id && 
           values.benefit_heading && 
           values.benefit_description && 
           values.dependents_covered && 
           values.dependents_covered.length > 0;
  }
  
  // For GRADED mode, check if grades have valid data
  if (values.choiceType === 'GRADED') {
    return values.grades && 
           values.grades.length > 0 && 
           values.grades.every(grade => 
             grade.entity_id && 
             grade.benefit_heading && 
             grade.benefit_description && 
             grade.dependents_covered && 
             grade.dependents_covered.length > 0
           );
  }
  
  return false;
};

function BenefitSetupDetail({ gmcGradeCodes, selectedBenefit, onProductCompletion, completedProducts, totalProducts }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(INIT_FORMIK_VALUES);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { saveBenefit, getPlanBenefitData } = useFetchWellness();

  useEffect(() => {
    (async function () {
      const { data } = await http.get("/benefits/get_vendor_benefit_mapping/", {
        params: {
          benefit_id: selectedBenefit,
        },
      });
      const vendors = data.map((ven) => ({
        value: ven?.vendor?.id,
        label: ven?.vendor?.name,
      }));
      setVendorOptions(vendors);
    })();
  }, [selectedBenefit]);

  useEffect(() => {
    if (!selectedBenefit) return;
    
    // Very short delay to prevent flicker without blocking UI
    const timeoutId = setTimeout(async () => {
      const benefitDetail = await getPlanBenefitData(selectedBenefit);
      
      // Check if user has a saved choice type preference for this benefit
      const savedChoiceType = localStorage.getItem(`benefit_choice_type_${selectedBenefit}`);
      
      // Determine choice type based on data structure if no saved preference
      let detectedChoiceType = "SAME"; // Default to SAME
      
      // Check if this should be GRADED based on GMC data
      if (benefitDetail?.length > 1) {
        // Multiple benefit details = GRADED
        detectedChoiceType = "GRADED";
      } else if (benefitDetail?.length === 1 && benefitDetail[0]?.grade_code) {
        // Single benefit detail with grade_code = GRADED
        detectedChoiceType = "GRADED";
      } else if (gmcGradeCodes?.length > 1) {
        // Multiple GMC grade codes available = should be GRADED
        detectedChoiceType = "GRADED";
      }
      
      // Use saved choice type if available, otherwise use detected type
      const choiceType = savedChoiceType || detectedChoiceType;
      
      if (benefitDetail?.length >= 1) {
        if (choiceType === "GRADED") {
          setInitialValues({
            choiceType: "GRADED",
            entity_id: "",
            benefit_details: [],
            vendor_id: benefitDetail[0].vendor,
            benefit_heading: "",
            redeem_option_data: "",
            benefit_description: "",
            dependents_covered: [],
            grades: benefitDetail.map((benef) => ({
              dependents_covered: benef?.dependents_covered || [],
              benefit_description: benef?.benefit_description,
              redeem_option_data: benef?.redeem_option_data,
              benefit_heading: benef?.benefit_heading,
              benefit_details: benef?.benefit_details || [""],
              entity_id: benef?.entity_id || [],
            })),
          });
          
          // Check if this GRADED product is already completed
          const isGradedCompleted = benefitDetail.every(grade => 
            grade?.entity_id && grade?.benefit_heading && grade?.benefit_description && grade?.dependents_covered?.length > 0
          );
          if (isGradedCompleted && onProductCompletion && !completedProducts?.has(selectedBenefit)) {
            onProductCompletion(selectedBenefit);
          }
        } else {
          // SAME type - use the first (and likely only) benefit detail
          const details = benefitDetail[0];
          setInitialValues({
            choiceType: "SAME",
            entity_id: details?.entity_id,
            benefit_details: details?.benefit_details || [],
            vendor_id: details?.vendor,
            benefit_heading: details?.benefit_heading,
            redeem_option_data: details?.redeem_option_data,
            benefit_description: details?.benefit_description,
            dependents_covered: details?.dependents_covered || [],
            grades: [],
          });
          
          // Check if this product is already completed and mark it as such
          if (details?.vendor && details?.entity_id && details?.benefit_heading && details?.benefit_description && details?.dependents_covered?.length > 0) {
            // Product has all required data, mark as completed
            if (onProductCompletion && !completedProducts?.has(selectedBenefit)) {
              onProductCompletion(selectedBenefit);
            }
          }
        }
      } else {
        // No existing data - check if we need to initialize grades for GRADED type
        if (choiceType === "GRADED" && gmcGradeCodes?.length > 0) {
          setInitialValues(() => ({
            ...INIT_FORMIK_VALUES,
            choiceType: "GRADED",
            benefit_id: selectedBenefit,
            grades: gmcGradeCodes.map(() => ({
              dependents_covered: [],
              benefit_description: "",
              redeem_option_data: "",
              benefit_heading: "",
              benefit_details: [""],
              entity_id: "",
            })),
          }));
        } else {
          setInitialValues(() => ({
            ...INIT_FORMIK_VALUES,
            benefit_id: selectedBenefit,
          }));
        }
      }
      
    }, 50); // 50ms delay to prevent flicker without blocking UI
    
    return () => clearTimeout(timeoutId);
  }, [selectedBenefit, getPlanBenefitData, onProductCompletion, completedProducts, gmcGradeCodes]);

  const navigateToSummary = (formikProps) => {
    // Check if all products are completed OR if current form has valid data
    const allProductsCompleted = completedProducts && completedProducts.size >= totalProducts;
    const hasValidFormData = formikProps?.values?.vendor_id && (formikProps?.dirty || isFormValid(formikProps?.values));
    
    if (!allProductsCompleted && !hasValidFormData) {
      // Show message that all products must be completed first OR form must have valid data
      alert(`Please complete all ${totalProducts} product setups or fill in all required fields before proceeding to summary.`);
      return;
    }
    
    // Navigate to summary
    if (isOldFlow) {
      navigate("/wellness/setup/summary", { replace: true });
    } else {
      navigate("/v1/wellness/setup/summary", { replace: true });
    }
  };

  const isOldFlow = useMemo(() => {
    return !location?.pathname.includes("v1");
  }, [location]);

  const submitHandler = async (values) => {
    try {
      setIsSaving(true);
      await saveBenefit({
        ...values,
        benefit_id: selectedBenefit,
        gmcGradeCodes,
      });
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Mark product as completed
      if (onProductCompletion) {
        onProductCompletion(selectedBenefit);
      }
      
    } catch (error) {
      // Handle error appropriately
      console.error("Error saving benefit:", error);
      // You might want to show an error message to the user
    } finally {
      setIsSaving(false);
    }
  };

  // Removed areAllRequiredFieldsFilled function as it's no longer used

  return (
    <>
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message="Benefit Saved Successfully!"
      />
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={submitHandler}>
      {(formikProps) => {
        // Removed disabledButton as it's no longer used for submit button

        return (
          <Form>
            <BenefitDetailWrapper>
              <BenefitFormWrapper>
                {gmcGradeCodes?.length > 0 && (
                  <BenefitSetupDetailSection title="Choose Type" className="choose-type-section">
                    <ChooseTypeWrapper>
                      {CHOICE_TYPE.map(({ display_name, value }) => (
                        <Field name="choiceType" key={value}>
                          {({ field, form }) => (
                            <DependentsCard
                              handleClick={() => {
                                if (!isSaving) {
                                  form.resetForm({
                                    values: {
                                      ...formikProps.values,
                                      grades: [],
                                    },
                                  });
                                  form.setFieldValue("choiceType", value);
                                  
                                  // Save the choice type preference for this benefit
                                  localStorage.setItem(`benefit_choice_type_${selectedBenefit}`, value);
                                }
                              }}
                              selected={formikProps.values.choiceType === value}
                              className="dependents-card"
                              style={{
                                opacity: isSaving ? 0.6 : 1,
                                pointerEvents: isSaving ? "none" : "auto",
                              }}
                            >
                              <Ptag>{display_name}</Ptag>
                            </DependentsCard>
                          )}
                        </Field>
                      ))}
                    </ChooseTypeWrapper>
                  </BenefitSetupDetailSection>
                )}
                <BenefitSetupDetailSection title="Select Vendor" className="select-vendor-section">
                  <BaseDropdown
                    fontSize="14"
                    options={vendorOptions}
                    formikProps={{ ...formikProps, name: "vendor_id" }}
                    disabled={isSaving}
                  />
                </BenefitSetupDetailSection>
                <BenefitSetupDetailGrade
                  formikProps={formikProps}
                  gmcGradeCodes={gmcGradeCodes}
                  disabled={isSaving}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <BenefitSaveBtn 
                    label={isSaving ? "Saving..." : "Save"} 
                    type="submit" 
                    disabled={isSaving}
                    style={{
                      opacity: isSaving ? 0.8 : 1,
                      pointerEvents: isSaving ? "none" : "auto",
                      position: 'relative'
                    }}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                  />
                </Box>
              </BenefitFormWrapper>
            </BenefitDetailWrapper>

            <LargeButton
              label="Submit"
              type="button"
              onClick={() => navigateToSummary(formikProps)}
              disabled={!(completedProducts && completedProducts.size >= totalProducts) && !(formikProps?.values?.vendor_id && (formikProps?.dirty || isFormValid(formikProps?.values)))}
              style={{
                opacity: (!(completedProducts && completedProducts.size >= totalProducts) && !(formikProps?.values?.vendor_id && (formikProps?.dirty || isFormValid(formikProps?.values)))) ? 0.6 : 1,
                pointerEvents: (!(completedProducts && completedProducts.size >= totalProducts) && !(formikProps?.values?.vendor_id && (formikProps?.dirty || isFormValid(formikProps?.values)))) ? "none" : "auto",
                marginTop: "1.5rem",
              }}
            />
         
          </Form>
        );
      }}
    </Formik>
    </>
  );
}

export default BenefitSetupDetail;